import { View } from '@play-co/timestep-core/ui';
import { Feature } from 'src/replicant/analytics';
import PopupMatch2BaseFullscreen from '../../components/PopupMatch2BaseFullscreen';
import TeamHeader from '../components/TeamHeader';
import TeamNav, { TeamNavConfig } from '../components/TeamNav';
import TeamTeamsPage from '../components/TeamTeamsPage';
import BaseTeamPage from '../components/BaseTeamPage';
import TeamCreatePage from '../components/TeamCreatePage';
import TeamSearchPage from '../components/TeamSearchPage';
import { isUserInTeam } from 'src/replicant/team/team.getters';
import { user } from 'src/StateObserver';
import MyTeamsPage from '../components/MyTeamsPage';
import TeamChatPage from '../components/TeamChatPage';
import { registerHandTarget } from 'src/game/tutorial/services/TutorialHandService';
import { getTutorialController } from 'src/game/tutorial/TutorialController';
import { PartialRecord } from 'src/replicant/utils/types';

type TeamPageId = 'teams' | 'create' | 'search' | 'chat' | 'myTeam';

export default class PopupTeam extends PopupMatch2BaseFullscreen {
  private readonly header: TeamHeader;
  private readonly nav: TeamNav;
  private pageView!: View;
  private pages!: Record<TeamPageId, () => BaseTeamPage>;
  private cachedPages: PartialRecord<TeamPageId, BaseTeamPage> = {};
  private currentPageId!: TeamPageId;

  constructor(opts: { superview: View; close: () => void }) {
    const headerHeight = 242;

    super({
      superview: opts.superview,
      zIndex: 10000,
      close: () => opts.close(),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.TOURNAMENT.POPUP,
      },
      bgImageOpts: {
        image: 'assets/ui/match2/team/team_bg.png',
        backgroundColor: '#D1937C',
      },
    });

    const superview = this.panel;
    const { width } = superview.style;

    this.createPages(headerHeight);

    this.header = new TeamHeader({
      superview,
      width: width - 1,
      height: headerHeight,
    });

    this.nav = new TeamNav({ superview, x: 3, y: headerHeight - 84 });
  }

  public init({ selectedTab }: { selectedTab?: number }) {
    super.init({});
    const inTeam = isUserInTeam(user());
    const config = this.getNavConfig(inTeam);
    config.selectedIndex = selectedTab ?? config.selectedIndex;
    this.nav.setup(config);

    if (!inTeam) {
      registerHandTarget('teamDiscoveryTab', this.nav.tabs[0]);
      void getTutorialController().handleEvent('teamPopupOpened');
    }
  }

  public override async fadeOut() {
    await super.fadeOut();
    this.header.killAnimations();
  }

  private getNavConfig(isInTeam: boolean): TeamNavConfig {
    const tabs: TeamNavConfig['tabs'] = isInTeam
      ? [
          {
            label: 'match2.team.tab_chat',
            onClick: () => this.goToPage('chat'),
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_CHAT,
            },
          },
          {
            label: 'match2.team.tab_my_team',
            onClick: () => this.goToPage('myTeam'),
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_MY_TEAM,
            },
          },
          {
            label: 'match2.team.tab_search',
            onClick: () => this.goToPage('search'),
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_SEARCH,
            },
          },
        ]
      : [
          {
            label: 'match2.team.tab_teams',
            onClick: () => {
              void getTutorialController().handleEvent('teamDiscoveryOpened');
              return this.goToPage('teams');
            },
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_TEAMS,
            },
          },
          {
            label: 'match2.team.tab_create',
            onClick: () => this.goToPage('create'),
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_CREATE,
            },
          },
          {
            label: 'match2.team.tab_search',
            onClick: () => this.goToPage('search'),
            tracking: {
              feature: Feature.MATCH2.TEAM._,
              $subFeature: Feature.MATCH2.TEAM.TAB_SEARCH,
            },
          },
        ];
    return {
      tabs,
      selectedIndex: 1,
    };
  }

  private createPages(offsetY: number) {
    this.pageView = new View({
      superview: this.panel,
      width: this.panel.style.width,
      height: this.panel.style.height - offsetY,
      y: offsetY,
    });

    this.pages = {
      teams: () => new TeamTeamsPage(this.pageView),
      create: () => new TeamCreatePage(this.pageView),
      search: () => new TeamSearchPage(this.pageView),
      chat: () => new TeamChatPage(this.pageView),
      myTeam: () => new MyTeamsPage(this.pageView),
    };

    for (const rawId in this.pages) {
      const pageId = rawId as TeamPageId;
      const pageConstructor = this.pages[pageId];
      this.pages[pageId] = () => {
        const page = pageConstructor();
        page.on('Close', () => this.opts.close?.());
        page.hide();
        return page;
      };
    }
  }

  private goToPage(id: TeamPageId) {
    if (this.currentPageId) {
      this.cachedPages[this.currentPageId]?.hide();
    }

    this.currentPageId = id;

    // Create page if it doesn't exist yet.
    if (!this.cachedPages[this.currentPageId]) {
      this.cachedPages[this.currentPageId] = this.pages[this.currentPageId]();
    }
    this.cachedPages[this.currentPageId]!.refresh();
    this.cachedPages[this.currentPageId]!.show();
  }
}
